import { createSignal, For, Show, createEffect } from "solid-js";
import type { QuizQuestion } from "../shared/quizParser";
import { renderMarkdown } from "../shared/markdown";

interface QuizQuestionPreviewProps {
  question: QuizQuestion;
  /** Optional callback when answer selection changes */
  onSelect?: (selectedAnswers: string[]) => void;
  /** Show answers as readonly (no interaction) */
  readonly?: boolean;
  /** Hide the correct answers indicator (for live quizzes) */
  hideCorrectAnswers?: boolean;
  /** Initial answers to pre-select (for restoring saved answers) */
  initialAnswers?: string[];
  /** Show only the question prompt during the pre-answer phase. */
  hideChoices?: boolean;
  /** Results data to show on answers */
  results?: {
    answerStats: Array<{
      answer: string;
      count: number;
      percentage: number;
      isCorrect: boolean;
    }>;
  };
}

export default function QuizQuestionPreview(props: QuizQuestionPreviewProps) {
  const [selectedAnswers, setSelectedAnswers] = createSignal<string[]>([]);

  // When initialAnswers changes (e.g., when question changes), update selection
  createEffect(() => {
    if (props.initialAnswers) {
      setSelectedAnswers(props.initialAnswers);
    } else {
      setSelectedAnswers([]);
    }
  });

  const handleAnswerClick = (answer: string) => {
    if (props.readonly) return;

    let newSelection: string[];
    
    if (props.question.isMultipleChoice) {
      // Toggle answer in selection
      if (selectedAnswers().includes(answer)) {
        newSelection = selectedAnswers().filter(a => a !== answer);
      } else {
        newSelection = [...selectedAnswers(), answer];
      }
    } else {
      // Single choice: replace selection
      newSelection = [answer];
    }
    
    setSelectedAnswers(newSelection);
    props.onSelect?.(newSelection);
  };

  const isAnswerSelected = (answer: string) => {
    return selectedAnswers().includes(answer);
  };

  const getAnswerStats = (answer: string, index: number) => {
    if (!props.results || !props.results.answerStats) return null;
    
    // Prioritize index-based match since answer text might have formatting differences
    if (props.results.answerStats[index]) {
      return props.results.answerStats[index];
    }
    
    // Fallback: try to find by exact match
    return props.results.answerStats.find(stat => stat.answer === answer) || null;
  };

  return (
    <div class="quiz-question-preview card">
      <div class="card-body">
        {/* Question content */}
        <div 
          class="question-content mb-4" 
          innerHTML={renderMarkdown(props.question.content)}
        />

        <Show when={!props.hideChoices}>
        {/* Answer type indicator */}
        <div class="mb-2">
          <small class="text-muted">
            <Show when={props.question.isMultipleChoice} fallback="Select one answer:">
              Select all correct answers:
            </Show>
          </small>
        </div>

        {/* Answers list */}
        <div class="answers-list">
          <For each={props.question.choices}>
            {(answer, index) => {
              const stats = getAnswerStats(answer, index());
              const hasResults = !!stats;
              const isCorrect = stats?.isCorrect || false;
              
              return (
                <div 
                  class={`answer-option position-relative p-3 mb-2 border rounded ${
                    isAnswerSelected(answer) ? 'border-primary bg-primary bg-opacity-10' : 'border-secondary'
                  } ${props.readonly ? '' : 'cursor-pointer'} ${hasResults && isCorrect ? 'border-success border-2' : ''}`}
                  onClick={() => handleAnswerClick(answer)}
                  style={{ 
                    cursor: props.readonly ? 'default' : 'pointer',
                    transition: 'all 0.2s',
                    overflow: 'hidden',
                    'min-height': '60px'
                  }}
                >
                  {/* Background bar for results */}
                  <Show when={hasResults}>
                    <div 
                      class={`position-absolute top-0 start-0 h-100 ${isCorrect ? 'bg-success' : 'bg-info'}`}
                      style={{ 
                        width: `${stats!.percentage}%`,
                        opacity: '0.25',
                        transition: 'width 0.6s ease-in-out',
                        'z-index': '0'
                      }}
                    />
                  </Show>
                  
                  <div class="d-flex align-items-center position-relative" style={{ 'z-index': '1' }}>
                    <Show when={props.question.isMultipleChoice} fallback={
                      <input 
                        type="radio" 
                        class="form-check-input me-3" 
                        checked={isAnswerSelected(answer)}
                        disabled={props.readonly}
                        style={{ "pointer-events": "none" }}
                      />
                    }>
                      <input 
                        type="checkbox" 
                        class="form-check-input me-3" 
                        checked={isAnswerSelected(answer)}
                        disabled={props.readonly}
                        style={{ "pointer-events": "none" }}
                      />
                    </Show>
                    <div class="flex-fill answer-text" innerHTML={renderMarkdown(answer)} />
                    
                    {/* Show stats badge */}
                    <Show when={hasResults}>
                      <div class="ms-2 d-flex align-items-center gap-2">
                        <Show when={isCorrect}>
                          <span class="badge bg-success">✓</span>
                        </Show>
                        <span class="badge bg-dark">
                          {stats!.count} ({stats!.percentage.toFixed(1)}%)
                        </span>
                      </div>
                    </Show>
                  </div>
                </div>
              );
            }}
          </For>
        </div>
        </Show>

        {/* Correct answers indicator */}
        <Show when={!props.hideCorrectAnswers && props.question.correctAnswers && props.question.correctAnswers.length > 0}>
          <div class="mt-3 alert alert-info py-2">
            <small>
              <strong>Correct answer{props.question.correctAnswers.length > 1 && 's'}:</strong>{' '}
              {props.question.correctAnswers.join(', ')}
            </small>
          </div>
        </Show>
      </div>
    </div>
  );
}
